// Wrapper app-side du recorder rrweb. rrweb est importé dynamiquement (zéro poids
// tant qu'on n'enregistre pas, comme modern-screenshot). Le recorder émet les
// events par batchs via onBatch ; un safety cap arrête tout seul au bout de 10 min.
//
// Le recorder n'instrumente PLUS lui-même la console et le réseau : cette capture
// vit dans `diagnostics-capture.ts` et lui est passée en `diagnostics`. Pendant
// l'enregistrement il s'y branche en sink pour que les erreurs restent
// entrelacées chronologiquement dans la session (`addCustomEvent`). Sans handle
// fourni — diagnostic non armé — il en crée un éphémère le temps de
// l'enregistrement, ce qui reproduit exactement le comportement historique.

import { type DiagnosticsHandle, installDiagnostics } from "./diagnostics-capture";

const SAFETY_CAP_MS = 10 * 60 * 1000;
const BATCH_INTERVAL_MS = 1000;

export type RrwebRecorderHandle = {
  /** Stoppe l'enregistrement, flush le dernier batch, et renvoie la durée. */
  stop: () => number;
};

export type StartRecorderOptions = {
  /** Appelé ~1×/s avec les events accumulés depuis le dernier batch. */
  onBatch: (events: unknown[]) => void;
  /** Appelé quand le safety cap se déclenche (le shell doit finaliser). */
  onCap: () => void;
  /** Capture réseau (ring buffer fetch/XHR) — défaut activé. */
  captureNetwork?: boolean;
  /** Capture de diagnostic déjà installée (régime armé). Absente → le recorder
   *  en crée une éphémère, détruite à l'arrêt. */
  diagnostics?: DiagnosticsHandle | null;
};

/**
 * Options de masquage passées à `record()`. Masquage AGRESSIF par défaut, opt-out
 * côté client (l'intégrateur révèle explicitement ce qui est safe) — jamais opt-in.
 * Contrat pour le dev de l'app embarquée :
 *  - `.leesto-mask` / `[data-leesto-block]` → élément ENTIER jamais capturé (placeholder).
 *  - `.leesto-mask-text` / `[data-private]` → structure gardée, TEXTE masqué.
 *  - tous les inputs sont masqués par défaut (`maskAllInputs`).
 * S'applique au full snapshot (« scan » initial) ET aux mutations incrémentales.
 */
export const RECORD_MASK_OPTIONS = {
  maskAllInputs: true,
  maskInputOptions: { password: true, email: true, tel: true, text: true },
  blockClass: "leesto-mask",
  blockSelector: "[data-leesto-block]",
  maskTextClass: "leesto-mask-text",
  maskTextSelector: "[data-private]",
} as const;

/** Démarre un enregistrement rrweb du document courant. Masquage agressif par
 *  défaut (RGPD/PCI/HIPAA) via `RECORD_MASK_OPTIONS` + rédaction PII des custom
 *  events (console/réseau) avant egress. */
export async function startRrwebRecorder(
  options: StartRecorderOptions,
): Promise<RrwebRecorderHandle> {
  const { record, addCustomEvent } = await import("rrweb");
  const startedAt = Date.now();
  let buffer: unknown[] = [];
  let stopped = false;
  let durationMs = 0;

  const flush = () => {
    if (buffer.length === 0) return;
    const batch = buffer;
    buffer = [];
    options.onBatch(batch);
  };

  const stopFn = record({
    emit(event) {
      buffer.push(event);
    },
    ...RECORD_MASK_OPTIONS,
  });

  // Capture diagnostique (console / erreurs / réseau) → custom events rrweb.
  // Branchée seulement si l'enregistrement a bien démarré.
  const provided = options.diagnostics ?? null;
  const capture = stopFn
    ? (provided ?? installDiagnostics({ captureNetwork: options.captureNetwork !== false }))
    : null;
  // Un handle qu'on a créé ici nous appartient : c'est le seul qu'on détruira.
  const ownsCapture = capture !== null && provided === null;

  capture?.setSink((tag, payload) => {
    // rrweb garde un flag `recording` GLOBAL au module : un autre stop
    // (double record() via HMR/StrictMode, stop avant l'init différée de
    // rrweb) le fait retomber et addCustomEvent jette alors — y compris
    // pendant notre flushRings. Capture best-effort : on perd l'event,
    // jamais l'app hôte.
    try {
      addCustomEvent(tag, payload);
    } catch {}
  });

  const interval = setInterval(flush, BATCH_INTERVAL_MS);
  // Box mutable pour que doStop() puisse annuler le cap même si la const
  // capTimer n'est pas encore assignée au moment de la définition de doStop.
  const cap = { timer: null as ReturnType<typeof setTimeout> | null };

  // Idempotent : un second appel (ex. le shell qui stoppe après onCap) renvoie la
  // durée figée sans re-stopper.
  const doStop = (): number => {
    if (stopped) return durationMs;
    stopped = true;
    clearInterval(interval);
    clearTimeout(cap.timer ?? undefined);
    capture?.flushRings();
    // Débrancher avant de détruire : un handle permanent survit à la session et
    // continue d'alimenter ses rings pour les signalements suivants.
    capture?.setSink(null);
    if (ownsCapture) capture?.teardown();
    stopFn?.();
    flush();
    durationMs = Date.now() - startedAt;
    return durationMs;
  };

  // Le cap stoppe lui-même le recorder (pas de fuite si le consommateur tarde),
  // puis notifie le shell pour qu'il finalise.
  cap.timer = setTimeout(() => {
    doStop();
    options.onCap();
  }, SAFETY_CAP_MS);

  return { stop: doStop };
}
